import * as zod from 'zod';

import { ActionRequiredError, Step } from '@sb/remote-control/types';

export namespace RunDemonstrationStep {
  export const name = 'Run ShowPath';
  export const description =
    'Move the arm to a target pose, then replay a recorded demonstration.';
  export const isDecorator = false;
  export const librarySection = Step.LibrarySection.Control;
  export const librarySort = '7';
  export const argumentKind = 'RunDemonstration';
  export const featureFlag = 'demonstrationRecording';

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    skillId: zod.string().optional(),
    recordingId: zod.string().optional(),
    positionListID: zod.string().nullable().default(null),
    positionListIndex: zod.number().optional(),
    repositionEnabled: zod.boolean().default(false),
    pin: zod.enum(['start', 'end', 'reference']).default('start'),
    // Sphere-radius trim, stored in meters (UI converts to/from the user's
    // system distance unit). null ⇒ disabled (replay the whole recording).
    // A value > 0 trims the replay to the last contiguous run of frames
    // within this distance of the pin center. `.positive().finite()` rejects
    // 0 and NaN at the schema layer (a zero sphere selects nothing; NaN slips
    // every `<=` comparison and becomes a confusing "closest approach" error).
    sphereRadius: zod.number().positive().finite().nullable().default(null),
    // Playback speed multiplier (1.0 = recorded cadence). Capped at 2× —
    // speed-ups downsample frames so the arm is commanded at speed× the taught
    // velocity, and ACB's joint limits backstop the rest.
    speed: zod.number().min(0.1).max(2).default(1.25),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const validator: Step.Validator = ({
    stepConfiguration,
    featureFlags,
  }) => {
    const args = stepConfiguration?.args;

    if (args?.argumentKind !== argumentKind) {
      return;
    }

    if (!args.skillId && !args.recordingId) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Select a demonstration to replay.',
        fieldId: featureFlags?.enableSkills ? 'skillId' : 'recordingId',
      });
    }

    if (args.repositionEnabled && !args.positionListID) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Select a position to reposition the demonstration to.',
        fieldId: 'positionListID',
      });
    }
  };

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    const {
      positionListID,
      positionListIndex,
      repositionEnabled,
      pin,
      sphereRadius,
      speed,
      skillId,
      recordingId,
    } = args;

    const replayArgs = {
      target:
        repositionEnabled && positionListID
          ? { positionListID, positionListIndex }
          : null,
      repositionEnabled,
      pin,
      sphereRadius,
      speed,
    };

    // The runner's Arguments is an either/or union: skill-driven steps emit
    // the skill id, and legacy steps emit just the recording id plus the
    // shared replay knobs. (A leftover recordingId on a skill step stays in
    // the FE configuration but is not forwarded to the runner.)
    if (skillId) {
      return {
        ...stepData,
        stepKind: 'RunDemonstration',
        args: {
          ...replayArgs,
          skillId,
        },
      };
    }

    if (!recordingId) {
      // validator() already blocks saving this state; guard for narrowing.
      throw new TypeError(
        'RunDemonstration step has neither a demonstration skill nor a recording',
      );
    }

    return {
      ...stepData,
      stepKind: 'RunDemonstration',
      args: { ...replayArgs, recordingId },
    };
  };
}

RunDemonstrationStep satisfies Step.StepKindInfo;
